import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import {
  Shield,
  Fingerprint,
  Lock,
  Clock,
  CheckCircle,
  AlertCircle,
  X,
  Eye,
  EyeOff,
} from 'lucide-react';
import { toast } from 'sonner';
import { authService } from '../utils/auth/authService';
import { biometricService } from '../utils/security/biometric';
import { autoLockService } from '../utils/security/autoLock';

interface SecuritySetupProps {
  userId: string;
  userName: string;
  onClose?: () => void;
}

export default function SecuritySetup({ userId, userName, onClose }: SecuritySetupProps) {
  const [biometricAvailable, setBiometricAvailable] = useState(false);
  const [biometricType, setBiometricType] = useState('Biometric');
  const [biometricEnabled, setBiometricEnabled] = useState(false);
  
  const [pinEnabled, setPinEnabled] = useState(false);
  const [showPinSetup, setShowPinSetup] = useState(false);
  const [pin, setPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [showPin, setShowPin] = useState(false);
  
  const [autoLockEnabled, setAutoLockEnabled] = useState(false);
  const [autoLockMinutes, setAutoLockMinutes] = useState(5);

  // Password prompt for enabling biometrics
  const [showPasswordPrompt, setShowPasswordPrompt] = useState(false);
  const [password, setPassword] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);

  useEffect(() => {
    loadSecurityStatus();
  }, [userId]);

  // Mark unused values as read to satisfy TS 6133 without removing them
  void React; // import-level
  void Badge; // imported UI component not currently rendered
  void userName; // prop currently unused in UI

  const loadSecurityStatus = async () => {
    // Check biometric
    const available = await biometricService.isAvailable();
    setBiometricAvailable(available);
    
    if (available) {
      const type = await biometricService.getBiometricType();
      setBiometricType(type);
      // Check if user has biometric enabled in their profile
      const user = authService.getCurrentUser();
      setBiometricEnabled(user?.security?.biometricEnabled || false);
    }

    // Check PIN
    const currentUser = authService.getCurrentUser();
    setPinEnabled(!!currentUser?.security?.pinEnabled && !!(currentUser?.security?.pin || currentUser?.pin));

    // Check auto-lock
    setAutoLockEnabled(autoLockService.isEnabled());
    setAutoLockMinutes(autoLockService.getTimeout());
  };

  const handleEnableBiometricClick = () => {
    setShowPasswordPrompt(true);
  };

  const handleConfirmEnableBiometric = async () => {
    if (!password) {
      toast.error('Please enter your password');
      return;
    }

    setIsVerifying(true);
    try {
      // Attempt native credential registration (on mobile)
      const success = await authService.enableBiometric(password);

      // Regardless of native availability, persist the biometric flag in Firestore under companies/{companyId}/users/{userId}/security/bio
      const user = authService.getCurrentUser();
      if (user) {
        await authService.updateUserProfile(user.id, {
          security: {
            ...(user.security || {}),
            biometricEnabled: true,
          }
        });
      }

      setBiometricEnabled(true);
      setShowPasswordPrompt(false);
      setPassword('');
      toast.success(`${biometricType} enabled${success ? '' : ' (device registration skipped)'}`);
    } catch (error) {
      console.error(error);
      toast.error('Could not enable biometric authentication');
    } finally {
      setIsVerifying(false);
    }
  };

  const handleDisableBiometric = async () => {
    // Remove native credential if present
    await biometricService.removeCredential();

    // Persist disabled state in Firestore under companies/{companyId}/users/{userId}/security/bio
    const user = authService.getCurrentUser();
    if (user) {
      await authService.updateUserProfile(user.id, {
        security: {
          ...(user.security || {}),
          biometricEnabled: false
        }
      });
    }

    setBiometricEnabled(false);
    toast.success(`${biometricType} disabled`);
  };

  const handleSetupPin = async () => {
    if (pin.length < 4 || pin.length > 6) {
      toast.error('PIN must be 4-6 digits');
      return;
    }

    if (pin !== confirmPin) {
      toast.error('PINs do not match');
      return;
    }

    // Persist PIN centrally via authService and update profile
    const ok = await authService.updatePin(pin);
    if (!ok) {
      toast.error('Failed to set up PIN');
      return;
    }
    const user = authService.getCurrentUser();
    if (user) {
      await authService.updateUserProfile(user.id, {
        security: {
          ...(user.security || {}),
          pinEnabled: true,
        }
      });
    }
    setPinEnabled(true);
    setShowPinSetup(false);
    setPin('');
    setConfirmPin('');
    toast.success('PIN code set successfully');
  };

  const handleDisablePin = async () => {
    // Clear PIN centrally
    await authService.updatePin('');
    const user = authService.getCurrentUser();
    if (user) {
      await authService.updateUserProfile(user.id, {
        security: {
          ...(user.security || {}),
          pinEnabled: false,
        }
      });
    }
    setPinEnabled(false);
    toast.success('PIN code disabled');
  };

  const handleAutoLockToggle = () => {
    if (autoLockEnabled) {
      autoLockService.disable();
      setAutoLockEnabled(false);
      toast.success('Auto-lock disabled');
    } else {
      autoLockService.enable();
      setAutoLockEnabled(true);
      toast.success('Auto-lock enabled');
    }
  };

  const handleAutoLockTimeChange = (minutes: number) => {
    setAutoLockMinutes(minutes);
    autoLockService.setTimeout(minutes);
    toast.success(`Auto-lock set to ${minutes} minutes`);
  };

  const handlePinInput = (value: string, setter: (val: string) => void) => {
    // Only allow digits
    const digits = value.replace(/\D/g, '').slice(0, 6);
    setter(digits);
  };

  return (
    <div className="fixed inset-0 z-50 bg-white flex flex-col">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-4 shadow-lg">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center space-x-2">
            <Shield className="h-6 w-6" />
            <h2 className="text-xl font-semibold">Security Settings</h2>
          </div>
          {onClose && (
            <button
              onClick={onClose}
              className="p-2 rounded-full hover:bg-white/20 active:bg-white/30 transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          )}
        </div>
        <p className="text-sm text-white/90">
          Protect your data with biometric and PIN security
        </p>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* Biometric Authentication */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Fingerprint className="h-5 w-5 text-blue-600" />
                <span>{biometricType} Authentication</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {biometricAvailable ? (
                <>
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <p className="font-medium text-gray-900">
                        Use {biometricType} to unlock
                      </p>
                      <p className="text-sm text-gray-600">
                        Quick and secure authentication
                      </p>
                    </div>
                    <div className={`w-12 h-6 rounded-full ${biometricEnabled ? 'bg-blue-600' : 'bg-gray-300'} transition-colors cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-2`}
                      onClick={biometricEnabled ? handleDisableBiometric : handleEnableBiometricClick}
                    >
                      <div className={`w-5 h-5 mt-0.5 bg-white rounded-full shadow-sm transition-transform ${biometricEnabled ? 'translate-x-6' : 'translate-x-0.5'}`}></div>
                    </div>
                  </div>

                  {biometricEnabled ? (
                    <div className="flex items-center space-x-2 p-3 bg-green-50 border border-green-200 rounded-lg">
                      <CheckCircle className="h-5 w-5 text-green-600" />
                      <span className="text-sm text-green-800">
                        {biometricType} is enabled
                      </span>
                    </div>
                  ) : showPasswordPrompt ? (
                    <div className="space-y-3 p-3 bg-gray-50 rounded-lg">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Confirm Password to Enable
                        </label>
                        <Input
                          type="password"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          placeholder="Enter your password"
                          className="w-full"
                        />
                      </div>
                      <div className="flex space-x-2">
                        <Button
                          onClick={() => {
                            setShowPasswordPrompt(false);
                            setPassword('');
                          }}
                          variant="outline"
                          className="flex-1"
                        >
                          Cancel
                        </Button>
                        <Button
                          onClick={handleConfirmEnableBiometric}
                          disabled={!password || isVerifying}
                          className="flex-1 bg-blue-600 hover:bg-blue-700"
                        >
                          {isVerifying ? 'Verifying...' : 'Confirm'}
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <Button
                      onClick={handleEnableBiometricClick}
                      className="w-full"
                    >
                      <Fingerprint className="h-5 w-5 mr-2" />
                      Enable {biometricType}
                    </Button>
                  )}
                </>
              ) : (
                <div className="flex items-start space-x-2 p-3 bg-gray-50 border border-gray-200 rounded-lg">
                  <AlertCircle className="h-5 w-5 text-gray-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm text-gray-900 font-medium">Not Available</p>
                    <p className="text-xs text-gray-600">
                      {biometricType} is not available on this device
                    </p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* PIN Code */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Lock className="h-5 w-5 text-purple-600" />
                <span>PIN Code</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between mb-3">
                <div>
                  <p className="font-medium text-gray-900">
                    Use PIN code to unlock
                  </p>
                  <p className="text-sm text-gray-600">
                    Backup authentication method
                  </p>
                </div>
                <div className={`w-12 h-6 rounded-full ${pinEnabled ? 'bg-purple-600' : 'bg-gray-300'} transition-colors cursor-pointer focus:outline-none focus:ring-2 focus:ring-purple-400 focus:ring-offset-2`}
                  onClick={() => {
                    if (pinEnabled) {
                      handleDisablePin();
                    } else {
                      setShowPinSetup(true);
                    }
                  }}
                >
                  <div className={`w-5 h-5 mt-0.5 bg-white rounded-full shadow-sm transition-transform ${pinEnabled ? 'translate-x-6' : 'translate-x-0.5'}`}></div>
                </div>
              </div>

              {pinEnabled ? (
                <div className="space-y-2">
                  <div className="flex items-center space-x-2 p-3 bg-purple-50 border border-purple-200 rounded-lg">
                    <CheckCircle className="h-5 w-5 text-purple-600" />
                    <span className="text-sm text-purple-800">
                      PIN code is enabled
                    </span>
                  </div>
                  <Button
                    onClick={() => setShowPinSetup(true)}
                    variant="outline"
                    className="w-full"
                  >
                    Change PIN
                  </Button>
                </div>
              ) : showPinSetup ? (
                <div className="space-y-3 p-3 bg-gray-50 rounded-lg">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Enter PIN (4-6 digits)
                    </label>
                    <div className="relative">
                      <Input
                        type={showPin ? 'text' : 'password'}
                        value={pin}
                        onChange={(e) => handlePinInput(e.target.value, setPin)}
                        placeholder="••••"
                        className="pr-10 text-2xl tracking-widest text-center"
                        maxLength={6}
                        inputMode="numeric"
                      />
                      <button
                        onClick={() => setShowPin(!showPin)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        {showPin ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                      </button>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Confirm PIN
                    </label>
                    <Input
                      type={showPin ? 'text' : 'password'}
                      value={confirmPin}
                      onChange={(e) => handlePinInput(e.target.value, setConfirmPin)}
                      placeholder="••••"
                      className="text-2xl tracking-widest text-center"
                      maxLength={6}
                      inputMode="numeric"
                    />
                  </div>

                  <div className="flex space-x-2">
                    <Button
                      onClick={() => {
                        setShowPinSetup(false);
                        setPin('');
                        setConfirmPin('');
                      }}
                      variant="outline"
                      className="flex-1"
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={handleSetupPin}
                      disabled={pin.length < 4 || pin !== confirmPin}
                      className="flex-1 bg-purple-600 hover:bg-purple-700"
                    >
                      Set PIN
                    </Button>
                  </div>
                </div>
              ) : (
                <Button
                  onClick={() => setShowPinSetup(true)}
                  className="w-full"
                >
                  <Lock className="h-5 w-5 mr-2" />
                  Set Up PIN
                </Button>
              )}
            </CardContent>
          </Card>

          {/* Auto-Lock */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Clock className="h-5 w-5 text-orange-600" />
                <span>Auto-Lock</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between mb-3">
                <div>
                  <p className="font-medium text-gray-900">
                    Lock after inactivity
                  </p>
                  <p className="text-sm text-gray-600">
                    Automatically lock the app
                  </p>
                </div>
                <div className={`w-12 h-6 rounded-full ${autoLockEnabled ? 'bg-orange-600' : 'bg-gray-300'} transition-colors cursor-pointer`}
                  onClick={handleAutoLockToggle}
                >
                  <div className={`w-5 h-5 mt-0.5 bg-white rounded-full shadow-sm transition-transform ${autoLockEnabled ? 'translate-x-6' : 'translate-x-0.5'}`}></div>
                </div>
              </div>

              {autoLockEnabled && (
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Lock after: {autoLockMinutes} {autoLockMinutes === 1 ? 'minute' : 'minutes'}
                    </label>
                    <input
                      type="range"
                      min="1"
                      max="30"
                      value={autoLockMinutes}
                      onChange={(e) => handleAutoLockTimeChange(parseInt(e.target.value))}
                      className="w-full"
                    />
                    <div className="flex justify-between text-xs text-gray-500 mt-1">
                      <span>1 min</span>
                      <span>30 min</span>
                    </div>
                  </div>

                  <div className="flex items-center space-x-2 p-3 bg-orange-50 border border-orange-200 rounded-lg">
                    <CheckCircle className="h-5 w-5 text-orange-600" />
                    <span className="text-sm text-orange-800">
                      Auto-lock enabled
                    </span>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Security Tips */}
          <Card className="bg-blue-50 border-blue-200">
            <CardContent className="p-4">
              <h4 className="font-medium text-blue-900 mb-2">Security Tips</h4>
              <ul className="space-y-1 text-sm text-blue-800">
                <li className="flex items-start space-x-2">
                  <span className="text-blue-600 mt-0.5">•</span>
                  <span>Enable both biometric and PIN for maximum security</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-blue-600 mt-0.5">•</span>
                  <span>Use a PIN that's difficult to guess</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-blue-600 mt-0.5">•</span>
                  <span>Enable auto-lock when working in the field</span>
                </li>
              </ul>
            </CardContent>
          </Card>
      </div>
    </div>
  );
}
